import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReaderTools from 'components/ReaderTools';
import ReaderView from 'components/ReaderView';
import Modal from 'components/common/Modal';
import useModal from 'components/common/Modal/useModal';
import ActivityWork from 'components/Activity/ActivityWork';
import { useStore, StoreTypes } from 'context';
import * as actionTypes from 'constants/actionTypes';
import { AnnotationType } from 'constants/annotationTypes';
import { BOOK_ORIENTATION } from 'constants/books';
import { Roles } from 'constants/role';
import { useFetchBooks } from 'customHooks/book';
import { useCanvasEvents } from 'customHooks/canvas';
import { usePullAnnotations } from 'customHooks/reader';
import { debounce } from 'util/debounce';
import { useBookCanvasEventHandler } from 'events/eventHandlers/BookCanvasEventHandler';
import { EventBus } from 'events/EventBus';
import { ReaderEvent, ReaderToolsEvent } from 'events/EventTypes';
import styles from './index.module.scss';
import classnames from 'classnames';
import { Indicator } from 'components/Indicator';
import * as types from 'constants/actionTypes';
import ActivityPanel from 'components/Activity/ActivityPanel';
import Submenu from 'components/Submenu';
import AreaZoomController from 'components/AreaZoomController';
import { useUpdateDisplayLanguage, useUpdateUserSettings } from 'customHooks/userSetting';
import { UserSettingsAPI } from 'api/UserSettingsAPI';
import { useAnnotationChecker } from 'customHooks/annotationChecker';
import MathToolsContainer from 'components/MathToolsContainer';
import { oneClassFirebaseConfig, oneTutorFirebaseConfig } from 'constants/firebaseConfig';
import { useFirebase } from 'customHooks/firebase';
import Loading from 'components/Loading';

import {
  RemoteVideo,
  useRosterState,
  useRemoteVideoTileState,
} from 'amazon-chime-sdk-component-library-react';


export const FullVideo = React.memo(({ role, fullScreenTarget }) => {
  const [attendeeId, setAttendeeId] = useState('');
  const { roster } = useRosterState();
  const { attendeeIdToTileId } = useRemoteVideoTileState();

  const getAttendTarget = useCallback((roleTarget) => {
    return Object.values(roster).find(item => {
      const role = item.externalUserId.split("$")[1]
      return (role !== Roles.ADMIN && role === roleTarget)
    });
  }, [roster])

  useEffect(() => {
    const roleTarget = role === Roles.TUTOR ? Roles.TUTOR_USER : Roles.TUTOR
    const attendTarget = getAttendTarget(role !== Roles.ADMIN ? roleTarget : fullScreenTarget)
    if (attendTarget) {
      setAttendeeId(attendTarget.chimeAttendeeId)
    }
  }, [getAttendTarget, roster, role, fullScreenTarget])

  return attendeeId && attendeeIdToTileId[attendeeId] ?
    <>
      <RemoteVideo tileId={attendeeIdToTileId[attendeeId]} />
    </>
    : null
})

const Reader = props => {
  const { bookId, paramsRole, paramsActivityId, paramsTeacherId, paramsInteractiveObjectId, roomId, studentId } = props
  const [canvasState, canvasDispatch] = useStore(StoreTypes.canvas);
  const [{ isFullVideo, firestore, fullScreenTarget }] = useStore(StoreTypes.course);
  const [{ isMathToolsShow, areaZoomInteractiveObjects, submenuInfo, isDoublePageMode, pageIndex, readerToolDirection, indicatorInfo: { isActive: isIndicatorActive, message: indicatorMessage } }, readerDispatch] = useStore(StoreTypes.reader);
  const [{ annotationType, activityInfo: { attendeeNumber } }, annotationDispatch] = useStore(StoreTypes.annotation);
  const [{ books, bookContent, orientation }, bookDispatch] = useStore(StoreTypes.books);
  const [{ role, token, userId }, userDispatch] = useStore(StoreTypes.user);

  const { isLoading, setLoading, fetchBooks, fetchBook, getBookCatalog } = useFetchBooks();
  const [{ isShowing, components }, { openModal, closeModal }] = useModal();
  const { addCanvasImage } = useCanvasEvents({ canvasState, canvasDispatch });
  const { pullInteractiveMetaObjects } = usePullAnnotations();
  const { updateDisplayLanguage } = useUpdateDisplayLanguage();
  const updateUserSettings = useUpdateUserSettings();

  const [updateLoading, setUpdateLoading] = useState(false);

  const createFirebaseConfig = useCallback((role) => {
    switch (role) {
      case Roles.TUTOR:
      case Roles.TUTOR_USER:
      case Roles.ADMIN:
        return oneTutorFirebaseConfig;
      case Roles.ONECLASS_TEACHER:
      case Roles.ONECLASS_STUDENT:
        return oneClassFirebaseConfig;
      case Roles.TEACHER:
      case Roles.PARENT:
      case Roles.STUDENT:
      case Roles.EDITOR:
      case Roles.GUEST:
      default:
        return null
    }
  }, [])


  const { initialize } = useFirebase();

  useEffect(() => {
    if (!role || role === Roles.GUEST) return;
    const firebaseConfig = createFirebaseConfig(role);
    initialize(firebaseConfig)
  }, [createFirebaseConfig, initialize, role])

  const imageFileHandlerRef = useRef(null);
  useBookCanvasEventHandler({ canvasState, canvasDispatch, imageFileHandlerRef });
  useEffect(() => {
    (async () => {
      if (token) {
        const res = await UserSettingsAPI.getUserSettings(token);
        if (res.status === 'success') {
          await updateUserSettings(res.data, false);
        }
      }
      updateDisplayLanguage();
    })();
  }, [token, updateDisplayLanguage, updateUserSettings]);

  useEffect(() => {
    if (paramsRole === Roles.STUDENT) {
      userDispatch({ type: actionTypes.UPDATE_USER_INFO, role: paramsRole });
      annotationDispatch({
        type: types.UPDATE_ANNOTATION_INFO,
        annotationType: AnnotationType.ACTIVITY
      });
      annotationDispatch({ type: types.TOGGLE_ACTIVITY_PANEL, enable: true });
    }

    return () => {
      bookDispatch({ type: types.RESET_BOOK_CONTENT });
      annotationDispatch({ type: types.RESET_ANNOTATION_INFO });
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!bookId) return;
    const book = books.find(book => book.bookId === bookId);
    if (!book) {
      (async () => {
        const fetchedBooks = await fetchBooks({ token, bookIds: [bookId] });

        bookDispatch({ type: actionTypes.SET_BOOK_ID, payload: bookId });
        let pages = [];
        if (isDoublePageMode) {
          pages = [pageIndex * 2, pageIndex * 2 + 1];
        } else {
          pages = [pageIndex];
        }
        pages = pages.filter(page => !bookContent[page]);
        if (pages.length > 0) {
          fetchBook({ givenBooks: fetchedBooks, bookId, pages });
        }
      })();
    } else {
      setLoading(false);
    }
    getBookCatalog({ bookId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  useEffect(() => {
    const book = books.find(book => book.bookId === bookId);
    if (book && role === Roles.EDITOR) {
      (async () => {
        readerDispatch({ type: types.SET_INDICATOR_INFO, indicatorInfo: { isActive: true } });
        readerDispatch({ type: types.SET_FULL_WIDTH_INFO, fullWidthInfo: { mode: false } });
        const result = await pullInteractiveMetaObjects({ id: paramsInteractiveObjectId, book });
        if (result) {
          annotationDispatch({ type: types.UPDATE_ANNOTATION_INFO, annotationId: paramsInteractiveObjectId, annotationType: AnnotationType.INTERACTIVE_OBJECT });
          EventBus.emit({ event: ReaderEvent.RefreshCanvasEvent, payload: { result } });
        }
        readerDispatch({ type: types.SET_INDICATOR_INFO, indicatorInfo: { isActive: false } });
      })();
    }
  }, [annotationDispatch, bookId, books, paramsInteractiveObjectId, pullInteractiveMetaObjects, readerDispatch, role]);

  useEffect(() => {
    const book = books.find(book => book.bookId === bookId);
    if (book) {
      if (book.width > book.height) {
        bookDispatch({
          type: types.SET_BOOK_ORIENTATION,
          orientation: BOOK_ORIENTATION.LANDSCAPE
        });
        readerDispatch({
          type: types.SWITCH_BOOK_PAGES_SHOW,
          isDoublePageMode: false
        });
      }
    }
  }, [bookDispatch, bookId, books, readerDispatch]);

  const handleImageFileChange = async e => {

    const file = e.target.files[0];

    if (file.size / 1024 > 500) {
      alert("提醒您！圖檔超過500kb無法存入備課紀錄，建議您將圖檔壓縮後再插入，謝謝。")
      return;
    }

    const upload = async (uploadPath, file) => {
      try {
        const snapshot = await firestore.storage().ref().child(uploadPath).put(file);
        const url = await firestore.storage().ref().child(uploadPath).getDownloadURL();
        return {
          status: true,
          data: snapshot,
          url
        };
      } catch (error) {
        return {
          status: false,
          data: error
        };
      }
    }

    setUpdateLoading(true)

    const uploadPath = `course/${roomId}/${userId}/${new Date().getTime()}_${file.name}`;
    const { status, url } = await upload(uploadPath, file)

    if (status) {
      addCanvasImage({ url }, () => {
        setUpdateLoading(false);
      });
    }


    imageFileHandlerRef.current.value = '';	// reset after read file
  };

  useEffect(() => {
    const resizeHandler = debounce(e => {
      const windowHeight = e.target.innerHeight;
      const windowWidth = e.target.innerWidth;
      if (windowHeight > windowWidth || orientation === BOOK_ORIENTATION.LANDSCAPE) {
        EventBus.emit({ event: ReaderToolsEvent.TogglePageModeEvent, payload: { isDoublePageMode: false } });
      } else if (windowHeight < windowWidth) {
        EventBus.emit({ event: ReaderToolsEvent.TogglePageModeEvent, payload: { isDoublePageMode: true } });
      }
    }, 100);
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    }
  }, [isDoublePageMode, orientation, pageIndex]);

  const readerClass = (role) => {
    switch (role) {
      case Roles.ONECLASS_TEACHER:
        return styles.teacherMode;
      case Roles.ONECLASS_STUDENT:
        return styles.studentMode;
      default:
        return styles.otherMode;
    }
  }
  return !isLoading && (
    <div id={"reader"} className={classnames(styles.reader, styles[readerToolDirection],
      readerClass(role))}>
      {
        updateLoading && <Loading />
      }
      {
        isFullVideo && <div className={styles.fullStudentVideo} >
          <FullVideo role={role} fullScreenTarget={fullScreenTarget} />
        </div>
      }
      {isIndicatorActive && <Indicator message={indicatorMessage} />}
      {
        isMathToolsShow && <MathToolsContainer />
      }
      <ReaderView className={readerToolDirection} channelName={roomId} clientId={userId} userId={studentId ? studentId : userId} role={role} />
      <ReaderTools className={readerToolDirection} />
      <Modal
        isShowing={isShowing}
        closeModal={closeModal}
        openModal={openModal}
      >{components}</Modal>
      <input type='file' onChange={handleImageFileChange} style={{ display: 'none' }} ref={imageFileHandlerRef} />
      {attendeeNumber && <ActivityWork />}
      {annotationType === AnnotationType.ACTIVITY && <ActivityPanel paramsActivityId={paramsActivityId} paramsTeacherId={paramsTeacherId} />}
      {submenuInfo && <Submenu submenuInfo={submenuInfo} />}
      {areaZoomInteractiveObjects && <AreaZoomController areaZoomInteractiveObjects={areaZoomInteractiveObjects} />}
    </div>
  );
};

export default Reader;
